/* eslint-disable spire/export-styles */
import { CustomState } from "@bToC/Store/Reducers";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const imageWrapperStyles = {
    css: css` 
        min-height:100px;
    `
}

const wrapperStyles = {
    css: css`
        text-align: center;
        justify-content: center;
        min-height: 100%;
        padding-top: 2rem;
        &:hover{
            background: '#016EAF';
        }
    `
}

const titleStyles = {
    css: css`
        font-weight: bold;
        color: #016EAF;
    `
}

const loadBtnStyles = {
    css: css`
        margin-top: 2rem;
        button{
            color: hsl(0,100%,47.7%);
            background: transparent !important;
            border: none;
            text-decoration: underline;
            text-underline-offset: 5px;
        }

    `
}

const enum fields {
    extraLargeColumnCount = "extraLargeColumnCount",
    extraLargeRowCount = "extraLargeRowCount",
    largeColumnCount = "largeColumnCount",
    mediumColumnCount = "mediumColumnCount",
    smallColumnCount = "smallColumnCount",
    extraSmallColumnCount = "extraSmallColumnCount",

    manufacturerTitle = "manufacturerTitle",
    manufacturerIcon = "manufacturerIcon",
    manufacturerLink = "manufacturerLink",
    manufacturers = "manufacturers",

    gridGap = "gridGap"
}

type ManufacturerModel = {
    fields: {
        [fields.manufacturerTitle]: string;
        [fields.manufacturerIcon]: string;
        [fields.manufacturerLink]: string;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gridGap]: number;
        [fields.extraLargeColumnCount]: GridWidths;
        [fields.extraLargeRowCount]: GridWidths;
        [fields.largeColumnCount]: GridWidths;
        [fields.mediumColumnCount]: GridWidths;
        [fields.smallColumnCount]: GridWidths;
        [fields.extraSmallColumnCount]: GridWidths;
        [fields.manufacturers]: ManufacturerModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const getColumnGridUnits = (numberOfColumns: number) => {
    return Math.floor(12 / Math.min(Math.max(numberOfColumns, 1), 12)) as GridWidths;
};

const ManufacturerGrid: React.FC<Props> = ({ fields, id, filterStr }) => {

    const [currnetRowCount, setCurrnetRowCount] = React.useState(3);

    const extraLargeColumnWidth = getColumnGridUnits(fields.extraLargeColumnCount);
    const largeColumnWidth = getColumnGridUnits(fields.largeColumnCount);
    const mediumColumnWidth = getColumnGridUnits(fields.mediumColumnCount);
    const smallColumnWidth = getColumnGridUnits(fields.smallColumnCount);
    const extraSmallColumnWidth = getColumnGridUnits(fields.extraSmallColumnCount);

    const { manufacturers } = fields;

    const gridStyles = {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        max-width: 100%;
                    `,
                    ],
                    "min",
                )};
        `
    }

    const rows: JSX.Element[] = [];
    let filteredRows: JSX.Element[] = [];
    let counter = 0;

    let filteredObj: Array<ManufacturerModel>;

    if (filterStr.length === 0) {
        filteredObj = manufacturers;
    } else if (filterStr.length === 1) {
        // Case for filter by Alphabet
        filteredObj = manufacturers.filter(obj => obj.fields.manufacturerTitle.toLocaleLowerCase().startsWith(filterStr))
    } else {
        // Case for filter by searching name
        filteredObj = manufacturers.filter(obj => obj.fields.manufacturerTitle.toLocaleLowerCase().includes(filterStr.toLocaleLowerCase()))
    }

    for (let row = 0; row < fields.extraLargeRowCount; row = row + 1) {
        const columns: JSX.Element[] = [];
        for (let column = 0; column < fields.extraLargeColumnCount; column = column + 1) {

            if (counter > filteredObj.length - 1) { break; }

            const { manufacturerIcon, manufacturerLink, manufacturerTitle } = filteredObj[counter].fields;

            columns.push(
                <GridItem
                    key={`GridItem-${id}-${column}-${row}`}
                    width={[
                        extraSmallColumnWidth,
                        smallColumnWidth,
                        mediumColumnWidth,
                        largeColumnWidth,
                        extraLargeColumnWidth,
                    ]}
                    {...wrapperStyles}
                >
                    <StyledWrapper >
                        <StyledWrapper {...imageWrapperStyles}>
                            <LazyImage src={manufacturerIcon} />
                        </StyledWrapper>

                        <Typography {...titleStyles}>{manufacturerTitle}</Typography>
                    </StyledWrapper>
                </GridItem>,
            );

            counter++;
        }

        rows.push(
            <GridContainer {...gridStyles} key={row} gap={fields.gridGap}>
                {columns}
            </GridContainer>,
        );
    }

    filteredRows = rows.slice(0, currnetRowCount);

    return <>
        {filteredRows}
        { currnetRowCount < rows.length ?
            <StyledWrapper {...loadBtnStyles}>
                <Button onClick={() => setCurrnetRowCount(currnetRowCount + 3)}>Load More</Button>
            </StyledWrapper>
            : ""
        }
    </>;

};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const mapStateToProps = (state: CustomState) => ({
    filterStr: state.searchManufacturerReducer.manufacturer,
});

const gridModule: WidgetModule = {
    component: connect(mapStateToProps)(ManufacturerGrid),
    definition: {
        group: "Basic",
        icon: "Grid",
        fieldDefinitions: [
            {
                name: fields.gridGap,
                displayName: "Grid Gap (e.g. 10 or 20)",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
            },
            {
                name: fields.extraLargeColumnCount,
                displayName: "Extra Large Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.extraLargeRowCount,
                displayName: "Extra Large Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.largeColumnCount,
                displayName: "Large Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.mediumColumnCount,
                displayName: "Medium Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.smallColumnCount,
                displayName: "Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.extraSmallColumnCount,
                displayName: "Extra Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 6,
            },

            {
                name: fields.manufacturers,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { manufacturerTitle } }) => manufacturerTitle,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.manufacturerTitle,
                        editorTemplate: "TextField",
                        isRequired: true,
                        displayName: "Manufacturer Title",
                        defaultValue: "",
                    },
                    {
                        name: fields.manufacturerIcon,
                        editorTemplate: "ImagePickerField",
                        tooltip: "Image Size has to be 330 x 229",
                        displayName: "Manufacturer Icon",
                        defaultValue: "",
                    },
                    {
                        name: fields.manufacturerLink,
                        editorTemplate: "TextField",
                        displayName: "Manufacturer Link",
                        defaultValue: "",
                    },
                ]
            }
        ],
    },
};

export default gridModule;
